import type { ProductNoEdibleInfo } from "@dransay/api";
import { useEffect, useMemo, useState } from "react";
import { useLoaderData } from "react-router";
import type { loader } from "~/routes/_storefront._products.product.$slug.$productId";
import { useCart } from "../cart/cart.context";
import {
	findVariantInCart,
	resolveVariantPriceVendorId,
	toVariantItem,
} from "./variants/variant-mapping";
import type { VariantItem } from "./variants/variant-selector.component";

type UseProductDetailsVendorStateOptions = {
	product: ProductNoEdibleInfo;
	enableVariantSelector: boolean;
};

export function useProductDetailsVendorState({
	product,
	enableVariantSelector,
}: UseProductDetailsVendorStateOptions) {
	const {
		defaultVendorId,
		allVendors,
		selectedVendorId,
		variants: loaderVariants,
	} = useLoaderData<typeof loader>();
	const { vendor: cartVendor, items: cartItems } = useCart();
	const cartVendorId = allVendors?.some(
		(v) => v.id.toString() === cartVendor?.id,
	)
		? cartVendor?.id
		: undefined;
	const selectedVendorSellsProduct =
		!!selectedVendorId &&
		allVendors?.some((v) => v.id.toString() === selectedVendorId);
	const [manualVendorId, setManualVendorId] = useState<string | undefined>(
		selectedVendorSellsProduct ? selectedVendorId : undefined,
	);
	const initialVendorId = manualVendorId ?? cartVendorId ?? defaultVendorId;
	const [localVendorId, setLocalVendorId] = useState<string | undefined>(
		initialVendorId,
	);
	const preferredVendorId = manualVendorId ?? cartVendorId;
	// biome-ignore lint/correctness/useExhaustiveDependencies: only reset vendor state on PDP navigation
	useEffect(() => {
		const nextManualVendorId = selectedVendorSellsProduct
			? selectedVendorId
			: undefined;
		setManualVendorId(nextManualVendorId);
		setLocalVendorId(nextManualVendorId ?? cartVendorId ?? defaultVendorId);
	}, [product.id]);
	useEffect(() => {
		const vendorExistsInCurrentOffers = (vendorId: string | undefined) =>
			!!vendorId &&
			!!allVendors?.some((vendor) => vendor.id.toString() === vendorId);
		const nextManualVendorId = vendorExistsInCurrentOffers(manualVendorId)
			? manualVendorId
			: selectedVendorSellsProduct
				? selectedVendorId
				: undefined;

		const nextLocalVendorId = vendorExistsInCurrentOffers(localVendorId)
			? localVendorId
			: (nextManualVendorId ?? cartVendorId ?? defaultVendorId);

		if (nextManualVendorId !== manualVendorId) {
			setManualVendorId(nextManualVendorId);
		}

		if (nextLocalVendorId !== localVendorId) {
			setLocalVendorId(nextLocalVendorId);
		}
	}, [
		allVendors,
		cartVendorId,
		defaultVendorId,
		localVendorId,
		manualVendorId,
		selectedVendorId,
		selectedVendorSellsProduct,
	]);
	const handleVendorChange = (vendorId: string | undefined) => {
		setLocalVendorId(vendorId);
		setManualVendorId(vendorId);
	};
	const rawCartVendorId = cartVendor?.id;
	const variantInCart = useMemo(
		() => findVariantInCart(loaderVariants, cartItems, rawCartVendorId),
		[rawCartVendorId, loaderVariants, cartItems],
	);
	const effectiveCartVendorId = variantInCart ? rawCartVendorId : undefined;
	const variants = useMemo<VariantItem[] | undefined>(() => {
		if (!enableVariantSelector || loaderVariants.length === 0) {
			return undefined;
		}
		return loaderVariants.map((v) => {
			const vendorId = resolveVariantPriceVendorId(
				v.id,
				product.id,
				localVendorId,
				preferredVendorId,
				effectiveCartVendorId,
			);
			return toVariantItem(v, vendorId, effectiveCartVendorId);
		});
	}, [
		enableVariantSelector,
		loaderVariants,
		localVendorId,
		preferredVendorId,
		product.id,
		effectiveCartVendorId,
	]);
	const effectivePreferredVendorId = preferredVendorId ?? effectiveCartVendorId;
	return {
		localVendorId,
		handleVendorChange,
		variants,
		effectivePreferredVendorId,
	};
}
